import React, { useEffect, useState } from 'react'

import Modal from 'react-bootstrap/Modal'
import Stack from 'react-bootstrap/Stack'
import Button from 'react-bootstrap/Button'

import { Spinner } from '../../LoadingContainer/Spinner'
import { useModal } from '../../../providers/ModalProvider/ModalProvider'
import { undoReleaseOrder } from '../../../utilities/api'
import Success from '../Success/Success'
import Error from '../Error/Error'

import AuthService from '../../../utilities/services/auth.service'

export default function UndoReleaseOrder({ order, onSuccess }) {
  const { closeModal } = useModal()
  const [modalState, setModalState] = useState('checking')
  const [restoredSeats, setRestoredSeats] = useState([])
  const [unavailableSeats, setUnavailableSeats] = useState([])
  const organization = AuthService.getOrg()

  useEffect(() => {
    let isMounted = true

    const runUndoRelease = async () => {
      const data = {
        order,
        organizationUUID: organization?.[0]?.uuid,
      }

      try {
        const response = await undoReleaseOrder(data)
        if (!isMounted) return

        const seats = response?.data?.restoredSeats || []
        setRestoredSeats(seats)
        setModalState('success')
        onSuccess?.()
      } catch (err) {
        if (!isMounted) return

        const unavailable = err?.response?.data?.unavailableSeats
        if (Array.isArray(unavailable) && unavailable.length > 0) {
          setUnavailableSeats(unavailable)
          setModalState('unavailable')
          return
        }

        setModalState('error')
      }
    }

    runUndoRelease()

    return () => {
      isMounted = false
    }
  }, [order?.id, organization?.[0]?.uuid, onSuccess])

  if (modalState === 'error') {
    return (
      <Error
        title="Could not undo release"
        description="Something went wrong while restoring tickets to this order. Please try again."
        onClose={closeModal}
      />
    )
  }

  if (modalState === 'unavailable') {
    return (
      <Modal.Body className="modal--lg">
        <Stack
          gap={4}
          className="text-center img--round-before img--round-before-lg background-icon-xxl background-icon-danger background-icon-info"
        >
          <h4 className="modal-body-heading-title mb-0">Seats unavailable</h4>
          <p className="mb-0">
            One or more seats from this order are no longer available and could
            not be added back.
          </p>
          <Stack
            gap={2}
            className="text-start mx-auto"
            style={{ maxWidth: 420 }}
          >
            {unavailableSeats.map((seat, index) => (
              <div key={`${seat?.label || 'seat'}-${index}`} className="small">
                <span className="fw-semi-bold">
                  {seat?.label || 'Unknown seat'}
                </span>
                {seat?.reason ? (
                  <span className="text-muted"> — {seat.reason}</span>
                ) : null}
              </div>
            ))}
          </Stack>
        </Stack>
        <Stack className="btn-group-flex">
          <Button size="lg" onClick={closeModal}>
            Close
          </Button>
        </Stack>
      </Modal.Body>
    )
  }

  if (modalState === 'success') {
    const seatSummary =
      restoredSeats.length > 0
        ? restoredSeats.join(', ')
        : 'All seats were added back to the order.'

    return (
      <Success
        title="Tickets added back to order"
        description="The following seats were restored to this order:"
        highlightText={seatSummary}
        onClose={closeModal}
      />
    )
  }

  return (
    <Modal.Body className="d-flex flex-column align-items-center justify-content-center text-center">
      <Stack gap={3} className="align-items-center">
        <Spinner variant="dark" />
        <div className="modal-body-heading">
          <h1 className="modal-body-heading-title m-0">
            Checking seat availability
          </h1>
        </div>
        <p className="text-muted mb-0">
          Verifying that the released seats are still available before adding
          them back to order {order?.orderId}.
        </p>
      </Stack>
    </Modal.Body>
  )
}
